import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { TicketType, TicketChannel, Attachment } from '@/types';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB per file
const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20MB total

export function NewTicketPage() {
  const { currentUser, emailVerified } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sourceParam = searchParams.get('source');
  const detectedChannel: TicketChannel =
    sourceParam === 'telegram' ? 'TELEGRAM' :
    sourceParam === 'discord' ? 'DISCORD' : 'WEB';

  const [type, setType] = useState<TicketType>('ISSUE');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [affiliateCode, setAffiliateCode] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const oversized = newFiles.find(f => f.size > MAX_FILE_SIZE);
      if (oversized) {
        setError(`File "${oversized.name}" exceeds 5MB limit`);
        return;
      }
      const totalSize = [...files, ...newFiles].reduce((sum, f) => sum + f.size, 0);
      if (totalSize > MAX_TOTAL_SIZE) {
        setError('Total attachment size exceeds 20MB limit');
        return;
      }
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (ticketId: string): Promise<Attachment[]> => {
    const attachments: Attachment[] = [];
    
    for (const file of files) {
      const storageRef = ref(storage, `tickets/${ticketId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      attachments.push({
        name: file.name,
        url,
        size: file.size,
        type: file.type,
        uploadedAt: new Date(),
      });
    }
    
    return attachments;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('You must be logged in to create a ticket');
      return;
    }

    if (!emailVerified) {
      setError('Please verify your email address before creating a ticket');
      return;
    }
    
    setError('');
    setSubmitting(true);

    try {
      // Validate affiliate code if provided
      const trimmedCode = affiliateCode.trim().toUpperCase();
      if (trimmedCode) {
        const codeDoc = await getDoc(doc(db, 'affiliateCodes', trimmedCode));
        if (!codeDoc.exists()) {
          setError('Invalid affiliate code. Please check and try again.');
          setSubmitting(false);
          return;
        }
      }

      const ticketData: any = {
        title: title.trim(),
        description: description.trim(),
        type,
        status: 'OPEN' as const,
        priority: 'MEDIUM' as const,
        channel: detectedChannel,
        ownerId: currentUser.uid,
        ownerEmail: currentUser.email || '',
        assignedToUserId: null,
        affiliateCode: trimmedCode || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, 'tickets'), ticketData);
      
      // Upload files if any
      if (files.length > 0) {
        const attachments = await uploadFiles(docRef.id);
        await addDoc(collection(db, 'tickets', docRef.id, 'attachments'), {
          attachments,
          uploadedAt: serverTimestamp(),
        });
      }
      
      navigate('/tickets/' + docRef.id);
    } catch (err: any) {
      console.error('Error creating ticket:', err);
      setError(err.message || 'Failed to create ticket');
      setSubmitting(false);
    }
  };

  return (
    <ProtectedRoute>
      <Layout>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div className="terminal-card">
            <div className="card-header">
              <span className="card-num">01</span>
              <span className="card-title">CREATE NEW TICKET</span>
              <div className="card-header-right">
                <span className="stat-chip">CHANNEL <strong>{detectedChannel}</strong></span>
              </div>
            </div>
            <div className="card-body">
              {error && (
                <div className="terminal-alert error">Error: {error}</div>
              )}

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div className="field-group">
                  <label className="field-label">TICKET TYPE</label>
                  <div className="dist-toggle">
                    <label className="radio-btn">
                      <input type="radio" name="ticketType" checked={type === 'ISSUE'} onChange={() => setType('ISSUE')} />
                      <span>ISSUE / BUG</span>
                    </label>
                    <label className="radio-btn">
                      <input type="radio" name="ticketType" checked={type === 'SERVICE_REQUEST'} onChange={() => setType('SERVICE_REQUEST')} />
                      <span>SERVICE REQUEST</span>
                    </label>
                  </div>
                </div>

                <div className="field-group">
                  <label className="field-label" htmlFor="title">TITLE *</label>
                  <input
                    type="text"
                    id="title"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="terminal-input"
                    placeholder="Brief summary"
                  />
                </div>

                <div className="field-group">
                  <label className="field-label" htmlFor="description">DESCRIPTION *</label>
                  <textarea
                    id="description"
                    required
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="terminal-textarea"
                    placeholder="Detailed information…"
                    rows={5}
                  />
                </div>

                <div className="field-group">
                  <label className="field-label" htmlFor="affiliateCode">GRID AFFILIATE CODE (OPTIONAL)</label>
                  <input
                    type="text"
                    id="affiliateCode"
                    value={affiliateCode}
                    onChange={(e) => setAffiliateCode(e.target.value.toUpperCase())}
                    className="terminal-input"
                    placeholder="AGENT_GRID_AGENT"
                  />
                  <span style={{ fontSize: '9px', color: 'var(--muted)', letterSpacing: '0.06em' }}>
                    Have an affiliate code? Enter it here to support your referrer
                  </span>
                </div>

                <div className="field-group">
                  <label className="field-label">ATTACHMENTS (OPTIONAL)</label>
                  <input
                    type="file"
                    id="attachments"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    accept="image/*,.pdf,.txt,.log,.json,.zip"
                  />
                  <label
                    htmlFor="attachments"
                    className="action-btn secondary"
                    style={{ cursor: 'pointer', display: 'inline-flex', width: 'fit-content' }}
                  >
                    Add Files
                  </label>
                  {files.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                      {files.map((file, index) => (
                        <span key={index} className="file-chip">
                          {file.name} ({(file.size / 1024).toFixed(1)} KB)
                          <button type="button" onClick={() => removeFile(index)}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="action-btn secondary"
                    disabled={submitting}
                    style={{ minWidth: 'auto' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="action-btn large"
                    disabled={submitting || !title.trim() || !description.trim()}
                    style={{ minWidth: 'auto' }}
                  >
                    {submitting ? 'Creating…' : 'Create Ticket'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Layout>
    </ProtectedRoute>
  );
}


